import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Send, CheckCircle } from 'lucide-react';

const contactSchema = z.object({
  name: z.string().max(100, 'Name must be less than 100 characters').optional(),
  email: z.string().email('Please enter a valid email address').max(255, 'Email must be less than 255 characters'),
  subject: z.string().max(200, 'Subject must be less than 200 characters').optional(),
  message: z.string().min(10, 'Message must be at least 10 characters').max(5000, 'Message must be less than 5000 characters'),
});

type ContactFormData = z.infer<typeof contactSchema>;

interface ContactFormProps {
  userEmail?: string;
  userName?: string;
  onSuccess?: () => void;
  compact?: boolean;
}

export function ContactForm({ userEmail, userName, onSuccess, compact = false }: ContactFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: userName || '',
      email: userEmail || '',
      subject: '',
      message: '',
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: data.name?.trim() || undefined,
          email: data.email.trim(),
          subject: data.subject?.trim() || undefined,
          message: data.message.trim(),
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to send message');
      }

      setIsSuccess(true);
      toast({
        title: 'Message sent!',
        description: "We'll get back to you as soon as possible.",
      });

      reset();
      onSuccess?.();

      // Reset success state after 3 seconds
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (error: any) {
      console.error('Error sending contact form:', error);
      toast({
        title: 'Failed to send message',
        description: error.message || 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mb-4">
          <CheckCircle className="h-8 w-8 text-accent" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Message Sent!</h3>
        <p className="text-muted-foreground text-sm">
          We'll get back to you as soon as possible.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className={compact ? 'grid grid-cols-2 gap-4' : 'space-y-4'}>
        <div className="space-y-2">
          <Label htmlFor="name" className="text-foreground">Name</Label>
          <Input
            id="name"
            placeholder="Your name"
            {...register('name')}
            className="bg-background/50 border-border focus:border-accent"
            disabled={isSubmitting}
          />
          {errors.name && (
            <p className="text-xs text-destructive">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-foreground">
            Email <span className="text-destructive">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            {...register('email')}
            className="bg-background/50 border-border focus:border-accent"
            disabled={isSubmitting || !!userEmail}
          />
          {errors.email && (
            <p className="text-xs text-destructive">{errors.email.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="subject" className="text-foreground">Subject</Label>
        <Input
          id="subject"
          placeholder="What's this about?"
          {...register('subject')}
          className="bg-background/50 border-border focus:border-accent"
          disabled={isSubmitting}
        />
        {errors.subject && (
          <p className="text-xs text-destructive">{errors.subject.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="text-foreground">
          Message <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="message"
          placeholder="How can we help you?"
          {...register('message')}
          className="bg-background/50 border-border focus:border-accent min-h-[120px] resize-none"
          disabled={isSubmitting}
        />
        {errors.message && (
          <p className="text-xs text-destructive">{errors.message.message}</p>
        )}
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="h-4 w-4 mr-2" />
            Send Message
          </>
        )}
      </Button>
    </form>
  );
}
